import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useIntl, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';

import { Modal } from '../../components';
import css from './AddItemModal.css';

import AutocompleteProductSelectField from '../../forms/EditListingDescriptionForm/AutocompleteCatalogProductField';
import CatalogItemSelectField from '../../forms/EditListingDescriptionForm/CatalogItemSelectField';

function generateId() {
  return Math.random().toString(36).slice(2);
}

export function AddItemModal({ open, onOpenChange, preselectedProductId, products = [], sdk }) {
  const intl = useIntl();
  const { providerId } = useParams();
  const queryClient = useQueryClient();

  const [serials, setSerials] = useState([{ id: generateId(), value: '' }]);
  const [apiError, setApiError] = useState(null);

  const inputRefs = useRef([]);
  const isProductLocked = Boolean(preselectedProductId);

  const selectedProductData = useMemo(() => {
    if (!isProductLocked) return null;
    return products.find(
      p => p.relationships.catalogItem?.data?.id.uuid === preselectedProductId
    );
  }, [products, preselectedProductId, isProductLocked]);

  const catalogProducts = useSelector(state => {
    const refs = state.productCatalog?.productCatalog || [];
    return getMarketplaceEntities(state, refs);
  });

  useEffect(() => {
    if (!open) {
      setSerials([{ id: generateId(), value: '' }]);
      setApiError(null);
      inputRefs.current = [];
    } else {
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
        }
      }, 50);
    }
  }, [open]);

  const duplicateValues = useMemo(() => {
    const seen = new Set();
    const duplicates = new Set();
    serials.forEach(s => {
      const val = s.value.trim();
      if (!val) return;
      if (seen.has(val)) duplicates.add(val);
      seen.add(val);
    });
    return duplicates;
  }, [serials]);

  const { mutate: handleCreatePieces, isLoading: isSubmitting } = useMutation({
    mutationFn: ({ codes, activeProductId }) => {
      setApiError(null);
      const pieces = codes.map(code => ({
        attributes: {
          catalogItemId: activeProductId,
          code: code.trim(),
          providerId,
        },
      }));
      return sdk.newSdk.pieces.createMany(pieces, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['ownListings']);
      queryClient.invalidateQueries(['pieces']);
      onOpenChange(false);
    },
    onError: error => {
      const rawErrorResponse = error?.response?.data || error?.data || error;
      let errorMessage = '';

      if (typeof rawErrorResponse === 'string') errorMessage = rawErrorResponse.trim();
      else if (rawErrorResponse?.errors?.[0]?.detail) errorMessage = rawErrorResponse.errors[0].detail;
      else if (rawErrorResponse?.message) errorMessage = rawErrorResponse.message;

      setApiError(
        errorMessage ||
        intl.formatMessage({
          id: 'AddItemModal.defaultApiError',
          defaultMessage: 'Nie można dodać sztuk. Jeden z podanych numerów seryjnych prawdopodobnie już istnieje w systemie.',
        })
      );
    },
  });

  const addRow = () => {
    if (isSubmitting) return;
    setSerials(prev => {
      const targetIndex = prev.length;
      setTimeout(() => {
        if (inputRefs.current[targetIndex]) {
          inputRefs.current[targetIndex].focus();
        }
      }, 50);
      return [...prev, { id: generateId(), value: '' }];
    });
  };

  const removeRow = id => {
    if (isSubmitting) return;
    setSerials(prev => prev.filter(s => s.id !== id));
    setApiError(null);
  };

  const updateRow = (id, value) => {
    setSerials(prev => prev.map(s => (s.id === id ? { ...s, value } : s)));
    if (apiError) setApiError(null);
  };

  const handlePaste = (e, index) => {
    const pasteData = e.clipboardData.getData('text');
    const lines = pasteData.split(/[\r\n]+/).map(line => line.trim()).filter(Boolean);

    if (lines.length > 1) {
      e.preventDefault();
      setSerials(prev => {
        const updated = [...prev];
        updated[index] = { ...updated[index], value: lines[0] };
        const newItems = lines.slice(1).map(val => ({ id: generateId(), value: val }));
        updated.splice(index + 1, 0, ...newItems);
        return updated;
      });
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      const currentValue = serials[index]?.value.trim();
      if (currentValue) {
        e.preventDefault(); // Zapobiega wysłaniu całego formularza
        const isLastRow = index === serials.length - 1;
        if (isLastRow) {
          addRow();
        } else if (inputRefs.current[index + 1]) {
          inputRefs.current[index + 1].focus();
        }
      }
    }
  };

  // Submit całego formularza
  const onSubmitForm = (values) => {
    const filledCodes = serials.map(s => s.value.trim()).filter(val => val.length > 0);
    const activeProductId = preselectedProductId || values.catalogItem;

    if (!activeProductId || filledCodes.length === 0 || !providerId || duplicateValues.size > 0) return;

    handleCreatePieces({ codes: filledCodes, activeProductId });
  };

  const filledCount = serials.filter(s => s.value.trim()).length;
  const hasLocalDuplicates = duplicateValues.size > 0;

  const getSubmitButtonLabel = () => {
    if (isSubmitting) return intl.formatMessage({ id: 'AddItemModal.submittingButton', defaultMessage: 'Zapisywanie...' });
    if (filledCount > 1) return intl.formatMessage(
      { id: 'AddItemModal.submitMultipleButton', defaultMessage: '{count, plural, few {Dodaj {count} sztuki} other {Dodaj {count} sztuk}}' },
      { count: filledCount }
    );
    if (isProductLocked) return intl.formatMessage({ id: 'AddItemModal.submitSingleLockedButton', defaultMessage: 'Dodaj sztukę' });
    return intl.formatMessage({ id: 'AddItemModal.submitSingleUnlockedButton', defaultMessage: 'Dodaj produkt' });
  };

  return (
    <Modal
      isOpen={open}
      onClose={() => !isSubmitting && onOpenChange(false)}
      id="AddItemModal"
      title={isProductLocked
        ? intl.formatMessage({ id: 'AddItemModal.titleLocked', defaultMessage: 'Dodaj nowe sztuki' })
        : intl.formatMessage({ id: 'AddItemModal.titleUnlocked', defaultMessage: 'Dodaj produkt' })
      }
      onManageDisableScrolling={() => { }}
      usePortal
    >
      <FinalForm
        onSubmit={onSubmitForm}
        initialValues={{ catalogProduct: null, catalogItem: '' }}
        render={({ handleSubmit, form, values, valid }) => {

          const canSubmit = (isProductLocked || valid) && filledCount > 0 && !hasLocalDuplicates && !isSubmitting;

          return (
            <form onSubmit={handleSubmit} className={css.modalForm}>

              <div className={css.inputWrapper}>
                {!isProductLocked ? (
                  <>
                    <AutocompleteProductSelectField
                      id="catalogProduct"
                      name="catalogProduct"
                      intl={intl}
                      form={form}
                      markRequired={true}
                      disabled={isSubmitting}
                      selectedProduct={values.catalogProduct}
                    />

                    <div className={css.marginTopVariant}>
                      <CatalogItemSelectField
                        id="catalogItem"
                        name="catalogItem"
                        intl={intl}
                        catalogProducts={catalogProducts}
                        catalogProduct={values.catalogProduct || ""}
                        markRequired={true}
                        form={form}
                        disabled={isSubmitting || !values.catalogProduct}
                      />
                    </div>
                  </>
                ) : (
                  <div>
                    <p className={css.helperText}>
                      {intl.formatMessage({ id: 'AddItemModal.helperText', defaultMessage: 'Dodajesz sztuki do określonego produktu.' })}
                    </p>
                    <strong className={css.productInfo}>
                      {selectedProductData?.attributes.code} - {selectedProductData?.attributes?.title || 'Wybranego modelu'}
                    </strong>
                  </div>
                )}
              </div>

              <div className={css.serialsSection}>
                <div className={css.serialsHeader}>
                  <label className={css.label}>
                    {intl.formatMessage({ id: 'AddItemModal.serialsHeaderLabel', defaultMessage: 'Numery ID sztuk' })}
                  </label>
                  {filledCount > 0 && (
                    <span className={css.badge}>
                      <FormattedMessage id="AddItemModal.badgeCount" defaultMessage="{count} {count, plural, one {sztuka} few {sztuki} many {sztuk} other {sztuki}}" values={{ count: filledCount }} />
                    </span>
                  )}
                </div>

                <div className={css.serialsList}>
                  {serials.map((serial, index) => {
                    const isDuplicate = duplicateValues.has(serial.value.trim());

                    return (
                      <div key={serial.id} className={css.serialRow}>
                        <span className={css.rowNumber}>{index + 1}.</span>
                        <input
                          ref={el => (inputRefs.current[index] = el)}
                          type="text"
                          value={serial.value}
                          disabled={isSubmitting}
                          onChange={e => updateRow(serial.id, e.target.value)}
                          onKeyDown={e => handleKeyDown(e, index)}
                          onPaste={e => handlePaste(e, index)}
                          placeholder={intl.formatMessage({ id: 'AddItemModal.serialPlaceholder', defaultMessage: 'Zeskanuj naklejkę z numerem ID sztuki' })}
                          className={`${css.textInput} ${isDuplicate ? css.inputError : ''}`}
                        />
                        {serials.length > 1 && (
                          <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={() => removeRow(serial.id)}
                            className={css.removeRowBtn}
                            title={intl.formatMessage({ id: 'AddItemModal.removeButtonTitle', defaultMessage: 'Usuń' })}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                {hasLocalDuplicates && (
                  <p className={css.fieldError}>
                    {intl.formatMessage({ id: 'AddItemModal.duplicateWarning', defaultMessage: 'Formularz zawiera zduplikowane numery seryjne. Każdy numer musi być unikalny.' })}
                  </p>
                )}

                <button type="button" disabled={isSubmitting} onClick={addRow} className={css.addRowBtn}>
                  {intl.formatMessage({ id: 'AddItemModal.addRowButton', defaultMessage: '+ Dodaj kolejną sztukę' })}
                </button>
              </div>

              {apiError && (
                <div className={css.errorBanner}>
                  <span className={css.errorIcon}>⚠️</span>
                  <div className={css.errorMessage}>{apiError}</div>
                </div>
              )}

              <div className={css.modalActions}>
                <button type="button" disabled={isSubmitting} onClick={() => onOpenChange(false)} className={css.cancelBtn}>
                  {intl.formatMessage({ id: 'AddItemModal.cancelButton', defaultMessage: 'Anuluj' })}
                </button>
                <button type="submit" disabled={!canSubmit} className={css.submitBtn}>
                  {getSubmitButtonLabel()}
                </button>
              </div>
            </form>
          );
        }}
      />
    </Modal>
  );
}